import { useState, useEffect, useRef } from "react";
import { PreviewHeader } from "./PreviewComponents/PreviewHeader";
import { PreviewContent } from "./PreviewContent";
import { PreviewForm } from "./PreviewComponents/PreviewForm";
import { PreviewFooter } from "./PreviewComponents/PreviewFooter";
import { PreviewDeviceControls, DeviceSize, getPreviewWidth } from "./PreviewDeviceControls";
import { colorSchemes, proColorSchemes } from "./constants";
import type { FunnelData } from "./types";
import { useToast } from "@/components/ui/use-toast";
import { createSubmission } from "@/lib/submissions";
import { Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CookieConsent } from "./PreviewComponents/CookieConsent";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
interface PreviewProps {
  data: FunnelData;
  isPublished?: boolean;
  currentPage?: string;
  showAfterSubmit?: boolean;
}
export const Preview = ({
  data,
  isPublished,
  currentPage,
  showAfterSubmit = false
}: PreviewProps) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(showAfterSubmit);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  const initialPageId = currentPage || data.pages[0]?.id || "default-page";
  const [selectedPageId, setSelectedPageId] = useState<string>(initialPageId);
  const {
    toast
  } = useToast();
  const isMounted = useRef(true);
  useEffect(() => {
    setIsSubmitted(showAfterSubmit);
  }, [showAfterSubmit]);
  const colors = data.isPro && data.theme in proColorSchemes ? proColorSchemes[data.theme as keyof typeof proColorSchemes] : data.theme in colorSchemes ? colorSchemes[data.theme as keyof typeof colorSchemes] : colorSchemes.slateLight;
  useEffect(() => {
    if (currentPage) {
      setSelectedPageId(currentPage);
    }
  }, [currentPage]);

  // Set favicon if uploaded
  useEffect(() => {
    if (data.favicon) {
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (link) {
        link.href = data.favicon;
      }
    }
  }, [data.favicon]);
  const selectedPage = data.pages.find(page => page.id === selectedPageId) || data.pages[0];
  const submissionLimitReached = !data.isPro && (data.submissionCount || 0) >= 15;
  const handleInputChange = (elementId: string, value: string) => {
    if (!isMounted.current) return;
    setFormValues(prev => ({
      ...prev,
      [elementId]: value
    }));
  };
  const handleSubmit = async (formData: Record<string, string>) => {
    if (submissionLimitReached) {
      toast({
        title: "Submission Limit Reached",
        description: "This funnel has reached its free submission limit. Upgrade to collect more submissions.",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      if (!data.id) throw new Error("Funnel ID is required");
      await createSubmission(data.id, formData);
      if (isMounted.current) {
        setIsSubmitted(true);
        setShowForm(false);
        setFormValues({});
      }
      if (data.emailConfig?.enabled && data.emailConfig?.notifyOwner) {
        console.log("Owner notification email would be sent for submission:", {
          funnelName: data.siteName,
          formData
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      if (isMounted.current) {
        toast({
          title: "Error",
          description: "Failed to submit form. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    }
  };
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(formValues);
  };
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const validPages = data.pages.filter(page => page.id && typeof page.id === 'string' && page.id.trim() !== '').map(page => ({
    ...page,
    id: page.id || `page-${Date.now()}`,
    title: page.title || 'Untitled Page'
  }));
  const showPageSelect = validPages.length > 1;
  return <div className="relative w-full h-full min-h-[500px] overflow-hidden bg-white/50 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-slate-200/60 px-0 py-0">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50/80 backdrop-blur-sm rounded-t-lg border-b border-slate-200/60">
        <div className="flex items-center gap-1.5">
          <Circle className="w-3 h-3 fill-red-500 stroke-red-600" />
          <Circle className="w-3 h-3 fill-yellow-500 stroke-yellow-600" />
          <Circle className="w-3 h-3 fill-green-500 stroke-green-600" />
        </div>
        <div className="flex items-center gap-2">
          {showPageSelect && !showAfterSubmit && <Select value={selectedPageId} onValueChange={setSelectedPageId}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select page" />
              </SelectTrigger>
              <SelectContent>
                {validPages.map(page => <SelectItem key={page.id} value={page.id}>
                    {page.title}
                  </SelectItem>)}
              </SelectContent>
            </Select>}
        </div>
        <PreviewDeviceControls deviceSize={deviceSize} onDeviceSizeChange={setDeviceSize} />
      </div>

      <ScrollArea className="h-[calc(100vh-12rem)]">
        <div className={cn("bg-gradient-to-b from-slate-50/50 to-white/50 backdrop-blur-sm rounded-b-lg transition-all duration-300 border border-slate-200/60", getPreviewWidth(deviceSize), "mx-auto")}>
          {selectedPage.showHeader && <PreviewHeader data={data} isMobileView={deviceSize === 'mobile'} />}
          <PreviewContent data={data} colors={colors} onShowForm={() => setShowForm(true)} isSubmitted={isSubmitted} currentPage={selectedPage} blogPosts={data.blogPosts || []} formElements={data.formElements} onFormSubmit={handleSubmit} isSubmitting={isSubmitting} />
          <PreviewForm showForm={showForm} setShowForm={setShowForm} data={data} colors={colors} formValues={formValues} handleInputChange={handleInputChange} handleSubmit={handleFormSubmit} isSubmitting={isSubmitting} submissionLimitReached={!data.isPro && (data.submissionCount || 0) >= 15} />
          {selectedPage.showFooter && <PreviewFooter data={data} colors={colors} />}
          {data.privacyConfig?.enabled && <CookieConsent privacyConfig={data.privacyConfig} colors={colors} />}
        </div>
      </ScrollArea>
    </div>;
};